<script setup>
import { ref, computed, onMounted } from 'vue'
import Textarea from 'primevue/textarea'
import InputNumber from 'primevue/inputnumber'
import ToggleSwitch from 'primevue/toggleswitch'
import Select from 'primevue/select'

import layoutShell from '@/components/layout/layoutShell.vue'
import LayoutFooter from '@/components/layout/layoutFooter.vue'
import AppDialog from '@/components/ui/AppDialog.vue'
import api from '@/api'
import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { useI18n } from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'

const notify    = useNotify()
const userStore = useUserStore()
const { t, activeLang } = useI18n()
const { confirmDelete } = useConfirmDelete()

/* ── Industries ─────────────────────────────────────────────────── */
const industries   = ref([])
const industryFilter = ref(null)

async function fetchIndustries() {
  try {
    const { data } = await api.getIndustries()
    industries.value = data?.data ?? []
  } catch { /* non-critical, dropdown stays empty */ }
}

function industryName(id) {
  if (!id) return ''
  const ind = industries.value.find(i => i.id === id)
  if (!ind) return ''
  return activeLang.value === 'de' ? ind.name_de : ind.name_en
}

const industryOptions = computed(() => [
  { id: null, label: t('admin.companies.filterIndustryAll') },
  ...industries.value.map(i => ({
    id:    i.id,
    label: activeLang.value === 'de' ? i.name_de : i.name_en,
  })),
])

/* ── Table ──────────────────────────────────────────────────────── */
const items   = ref([])
const loading = ref(false)

const columns = [
  { field: 'name',        header: t('admin.companies.colName'),        sortable: true,  default: true  },
  { field: 'industry_id', header: t('admin.companies.colIndustry'),    sortable: false, default: true  },
  { field: 'city',        header: t('admin.companies.colCity'),        sortable: true,  default: true  },
  { field: 'country',     header: t('admin.companies.colCountry'),     sortable: true,  default: true  },
  { field: 'user_count',  header: t('admin.companies.colUsers'),       sortable: false, default: true  },
  { field: 'is_customer', header: t('admin.companies.colType'),        sortable: false, default: true  },
  { field: 'id',          header: t('admin.companies.colId'),          sortable: true,  default: false },
  { field: 'description', header: t('admin.companies.colDescription'), sortable: false, default: false },
]

const filteredItems = computed(() => {
  if (!industryFilter.value) return items.value
  return items.value.filter(c => c.industry_id === industryFilter.value)
})

async function fetchCompanies() {
  loading.value = true
  try {
    const { data } = await api.getCompanies()
    const raw = Array.isArray(data) ? data : (data?.companies ?? [])
    items.value = raw
  } catch (e) {
    notify.error(t('common.error'), e?.response?.data?.message || t('admin.companies.loadFailed'))
  } finally {
    loading.value = false
  }
}

/* ── Dialog ─────────────────────────────────────────────────────── */
const showDialog  = ref(false)
const isAdding    = ref(false)
const saving      = ref(false)

const emptyForm = () => ({
  name: '', description: '', address: '', city: '', state: '',
  zip: '', country: '', phone: '', website: '', industry: '',
  industry_id: null,
  employee_count: null, founded_date: null, is_customer: true, is_partner: false, partner_company_id: null,
})

const form = ref(emptyForm())

function openCreate() {
  if (!userStore.can('admin.create')) return
  isAdding.value = true
  form.value = emptyForm()
  showDialog.value = true
}

function openEdit(row) {
  if (!userStore.can('admin.change')) return
  isAdding.value = false
  form.value = {
    id:                 row.id,
    name:               row.name               || '',
    description:        row.description        || '',
    address:            row.address            || '',
    city:               row.city               || '',
    state:              row.state              || '',
    zip:                row.zip                || '',
    country:            row.country            || '',
    phone:              row.phone              || '',
    website:            row.website            || '',
    industry:           row.industry           || '',
    industry_id:        row.industry_id        ?? null,
    employee_count:     row.employee_count     ?? null,
    founded_date:       row.founded_date ? row.founded_date.split('T')[0] : null,
    is_customer:        row.is_customer        !== false,
    is_partner:         row.is_partner         === true,
    partner_company_id: row.partner_company_id ?? null,
  }
  showDialog.value = true
}

async function save() {
  if (!form.value.name?.trim()) {
    notify.warn(t('common.validation'), t('admin.companies.nameRequired'))
    return
  }
  saving.value = true
  const { id, ...payload } = form.value
  payload.employee_count     = payload.employee_count     == null ? null : Number(payload.employee_count)
  payload.partner_company_id = payload.partner_company_id == null ? null : Number(payload.partner_company_id)
  try {
    if (isAdding.value) {
      await api.createCompany(payload)
      notify.success(t('admin.companies.saved'), t('admin.companies.companyCreated'))
    } else {
      await api.updateCompany(id, payload)
      notify.success(t('admin.companies.saved'), t('admin.companies.companyUpdated'))
    }
    showDialog.value = false
    await fetchCompanies()
  } catch (e) {
    notify.error(t('admin.companies.saveFailed'), e?.response?.data?.message || e?.message)
  } finally {
    saving.value = false
  }
}

async function removeCompanies(rows) {
  const names = rows.map(r => r.name).join(', ')
  if (!await confirmDelete(t('admin.companies.confirmDelete').replace('{name}', names))) return
  try {
    await Promise.all(rows.map(r => api.deleteCompany(r.id)))
    notify.success(t('admin.companies.deleted'), t('admin.companies.companyDeleted'))
    await fetchCompanies()
  } catch (e) {
    notify.error(t('admin.companies.deleteFailed'), e?.response?.data?.message || e?.message)
  }
}

const tableActions = computed(() => [
  userStore.can('admin.change') && {
    label:          t('admin.companies.editTooltip'),
    icon:           'pi pi-file-edit',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  userStore.can('admin.delete') && {
    label:     t('admin.companies.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   removeCompanies,
  },
].filter(Boolean))

/* ── Section tabs for the dialog ───────────────────────────────── */
const dialogTab = ref('general')
const dialogTabs = computed(() => [
  { key: 'general',  label: t('admin.companies.tabGeneral')  },
  { key: 'address',  label: t('admin.companies.tabAddress')  },
  { key: 'details',  label: t('admin.companies.tabDetails')  },
])

onMounted(() => {
  fetchIndustries()
  if (userStore.can('admin.view')) fetchCompanies()
})
</script>

<template>
  <layoutShell>
    <div class="layout-content-wrapper">
      <main class="content">

        <div v-if="!userStore.can('admin.view')" class="p-4">
          <h2>{{ t('admin.noPermission') }}</h2>
          <p>{{ t('admin.noPermissionHint').replace('{perm}', 'admin.view') }}</p>
        </div>

        <AppTable
          v-else
          table-key="admin-companies"
          :columns="columns"
          :data="filteredItems"
          :loading="loading"
          :actions="tableActions"
          @refresh="fetchCompanies"
        >
          <template #toolbar>
            <pButton
              v-if="userStore.can('admin.create')"
              icon="pi pi-plus"
              :label="t('admin.companies.addBtn')"
              size="small"
              @click="openCreate"
            />
            <Select
              v-model="industryFilter"
              :options="industryOptions"
              option-label="label"
              option-value="id"
              :placeholder="t('admin.companies.filterIndustry')"
              size="small"
              show-clear
              class="filter-select"
            />
          </template>

          <!-- Industry name cell -->
          <template #cell-industry_id="{ row }">
            <span v-if="industryName(row.industry_id)">{{ industryName(row.industry_id) }}</span>
            <span v-else-if="row.industry">{{ row.industry }}</span>
            <span v-else class="text-secondary" style="opacity:.4">—</span>
          </template>

          <!-- User count badge -->
          <template #cell-user_count="{ row }">
            <span v-if="row.user_count > 0" class="user-count-badge">
              <i class="pi pi-users" />
              {{ row.user_count }}
            </span>
            <span v-else class="text-secondary" style="opacity:.4">—</span>
          </template>

          <!-- Type badges (Customer / Partner) -->
          <template #cell-is_customer="{ row }">
            <div class="type-badges">
              <Tag v-if="row.is_customer" :value="t('admin.companies.tagCustomer')" severity="success" />
              <Tag v-if="row.is_partner"  :value="t('admin.companies.tagPartner')"  severity="info" />
              <span v-if="!row.is_customer && !row.is_partner" class="text-secondary" style="opacity:.4">—</span>
            </div>
          </template>

        </AppTable>

      </main>
    </div>

    <div class="layout-footer">
      <LayoutFooter />
    </div>

    <!-- ── Dialog ───────────────────────────────────────────────── -->
    <AppDialog
      v-model="showDialog"
      :title="isAdding ? t('admin.companies.dialogCreate') : t('admin.companies.dialogEdit')"
      size="xl"
      :showFooter="false"
      :maximizable="true"
    >
      <!-- Tab navigation -->
      <div class="dialog-tabs">
        <button
          v-for="tab in dialogTabs"
          :key="tab.key"
          class="dialog-tab"
          :class="{ 'is-active': dialogTab === tab.key }"
          type="button"
          @click="dialogTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <form @submit.prevent="save" class="dialog-form">

        <!-- Tab: General -->
        <template v-if="dialogTab === 'general'">
          <div class="form-row-2">
            <div class="form-field required">
              <label>{{ t('admin.companies.formName') }}</label>
              <InputText v-model="form.name" class="w-full" required />
            </div>
            <div class="form-field">
              <label>{{ t('admin.companies.formIndustry') }}</label>
              <Select
                v-model="form.industry_id"
                :options="industryOptions.filter(o => o.id !== null)"
                option-label="label"
                option-value="id"
                :placeholder="t('admin.companies.filterIndustryAll')"
                class="w-full"
                show-clear
              />
            </div>
          </div>
          <div class="form-field">
            <label>{{ t('admin.companies.formDescription') }}</label>
            <Textarea v-model="form.description" rows="3" class="w-full" />
          </div>
          <div class="form-row-2">
            <div class="form-field">
              <label>{{ t('admin.companies.formWebsite') }}</label>
              <InputText v-model="form.website" class="w-full" />
            </div>
            <div class="form-field">
              <label>{{ t('admin.companies.formPhone') }}</label>
              <InputText v-model="form.phone" class="w-full" />
            </div>
          </div>
          <div class="form-row-2">
            <div class="form-field">
              <label>{{ t('admin.companies.formCustomer') }}</label>
              <div class="flex align-items-center gap-2 mt-1">
                <ToggleSwitch v-model="form.is_customer" />
                <span class="text-sm">{{ form.is_customer ? t('common.yes') : t('common.no') }}</span>
              </div>
            </div>
            <div class="form-field">
              <label>{{ t('admin.companies.formPartner') }}</label>
              <div class="flex align-items-center gap-2 mt-1">
                <ToggleSwitch v-model="form.is_partner" />
                <span class="text-sm">{{ form.is_partner ? t('common.yes') : t('common.no') }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Tab: Address -->
        <template v-if="dialogTab === 'address'">
          <div class="form-field">
            <label>{{ t('admin.companies.formAddress') }}</label>
            <InputText v-model="form.address" class="w-full" />
          </div>
          <div class="form-row-3">
            <div class="form-field">
              <label>{{ t('admin.companies.formZip') }}</label>
              <InputText v-model="form.zip" class="w-full" />
            </div>
            <div class="form-field">
              <label>{{ t('admin.companies.formCity') }}</label>
              <InputText v-model="form.city" class="w-full" />
            </div>
            <div class="form-field">
              <label>{{ t('admin.companies.formState') }}</label>
              <InputText v-model="form.state" class="w-full" />
            </div>
          </div>
          <div class="form-field" style="max-width: 280px">
            <label>{{ t('admin.companies.formCountry') }}</label>
            <InputText v-model="form.country" class="w-full" />
          </div>
        </template>

        <!-- Tab: Details -->
        <template v-if="dialogTab === 'details'">
          <div class="form-row-2">
            <div class="form-field">
              <label>{{ t('admin.companies.formEmployees') }}</label>
              <InputNumber v-model="form.employee_count" :min="0" inputClass="w-full" class="w-full" />
            </div>
            <div class="form-field">
              <label>{{ t('admin.companies.formFounded') }}</label>
              <InputText v-model="form.founded_date" type="date" class="w-full" />
            </div>
          </div>
          <div class="form-field" style="max-width: 280px">
            <label>{{ t('admin.companies.formPartnerId') }}</label>
            <InputNumber v-model="form.partner_company_id" :min="1" inputClass="w-full" class="w-full" />
          </div>
        </template>

        <!-- Footer actions -->
        <div class="dialog-actions">
          <pButton type="button" :label="t('common.cancel')" severity="secondary" @click="showDialog = false" />
          <pButton type="submit" :label="t('common.save')" icon="pi pi-save" :loading="saving" />
        </div>
      </form>
    </AppDialog>
  </layoutShell>
</template>

<style scoped>
/* ── Toolbar filter ──────────────────────────────────────────────── */
.filter-select {
  min-width: 160px;
}

/* ── Dialog tabs ─────────────────────────────────────────────────── */
.dialog-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--p-content-border-color, rgba(0,0,0,.08));
  margin-bottom: 20px;
}

.dialog-tab {
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: .82rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color .12s, border-color .12s;
}

.dialog-tab:hover { color: var(--p-text-color); }

.dialog-tab.is-active {
  color: var(--p-primary-color, #3b82f6);
  border-bottom-color: var(--p-primary-color, #3b82f6);
}

/* ── Form ─────────────────────────────────────────────────────────── */
.dialog-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

@media (max-width: 640px) {
  .form-row-2, .form-row-3 { grid-template-columns: 1fr; }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  font-size: .78rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
}

.form-field.required label::after {
  content: ' *';
  color: var(--p-red-500, #ef4444);
}

/* ── Badges ──────────────────────────────────────────────────────── */
.type-badges {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.user-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: .8rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
}

/* ── Dialog footer ───────────────────────────────────────────────── */
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--p-content-border-color, rgba(0,0,0,.06));
  margin-top: 4px;
}

/* ── Dark mode ───────────────────────────────────────────────────── */
:global(.my-app-dark) .dialog-tabs {
  border-bottom-color: var(--p-surface-700, #334155);
}

:global(.my-app-dark) .dialog-actions {
  border-top-color: var(--p-surface-700, #334155);
}
</style>
